import {
  Box,
  Button,
  createStyles,
  Group,
  Image,
  ImageProps,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { isEmpty } from 'lodash/fp';
import React from 'react';

import { OrderType } from '@portals/api/partners';
import { useGenericContext } from '@portals/framework';
import { ReactComponent as Gallery } from '@portals/icons/bold/gallery.svg';
import { ProductPricingModel } from '@portals/types';
import { formatCurrency } from '@portals/utils';

interface OrderProductBreakdownRowProps {
  orderItemId: string;
  onSelect: () => void;
  isSelected: boolean;
  withSerialNumber: boolean;
  sn: string | null;
}

export function OrderProductBreakdownRow({
  orderItemId,
  onSelect,
  isSelected,
  withSerialNumber,
  sn,
}: OrderProductBreakdownRowProps) {
  const { classes, cx } = useStyles();
  const theme = useMantineTheme();
  const order = useGenericContext<OrderType>();

  if (!order || isEmpty(order?.items)) return null;

  const orderItem = order.items.find(
    (item: OrderType['items'][number]) => item.id === orderItemId
  );

  if (!orderItem) return null;

  const getInterval = (
    paymentInterval: OrderType['items'][number]['payment_interval']
  ) => {
    if (paymentInterval === 'monthly') return ' / month';
    else if (paymentInterval === 'yearly') return ' / year';

    return '';
  };

  return (
    <Box
      w="100%"
      className={cx(classes.container, { selected: isSelected })}
      sx={{
        gridTemplateColumns: withSerialNumber
          ? '2fr 1.5fr 1.5fr 1.5fr 100px'
          : '2fr 1.5fr 1.5fr 100px',
      }}
      py="sm"
      px="xs"
      onClick={onSelect}
    >
      <Group noWrap spacing="sm" p={0}>
        <Image
          withPlaceholder
          src={orderItem.product.image_url}
          styles={imageStyles}
          radius="md"
          placeholder={
            <Gallery
              width="50%"
              height="50%"
              color={theme.colors.blue_gray[2]}
            />
          }
        />

        <Text size="sm" color="gray.7">
          {orderItem.product.name}
        </Text>
      </Group>

      <Stack justify="center">
        <Text size="sm" color="gray.5" transform="capitalize">
          {orderItem.product.device_model_name}
        </Text>
      </Stack>

      {withSerialNumber ? (
        <Stack justify="center">
          <Text size="sm" color="gray.5" transform="capitalize">
            {sn || 'N/A'}
          </Text>
        </Stack>
      ) : null}

      <Stack justify="center">
        {orderItem.product.pricing_model === ProductPricingModel.UsageBased ? (
          <Text size="sm" color="gray.5">
            {formatCurrency(orderItem.usage_based_price_in_scu, order.currency)}{' '}
            / {orderItem.product.usage_based_unit_name}
          </Text>
        ) : (
          <Text size="sm" color="gray.5">
            {formatCurrency(orderItem.price_in_scu, order.currency)}
            {getInterval(orderItem.payment_interval)}
          </Text>
        )}
      </Stack>

      <Stack justify="center">
        {isSelected ? null : (
          <Button size="xs" variant="light" radius="xl">
            Show details
          </Button>
        )}
      </Stack>
    </Box>
  );
}

const imageStyles: ImageProps['styles'] = {
  root: {
    width: '34px !important',
    height: '34px !important',
  },
  figure: { height: 34, width: 34 },
  imageWrapper: { height: 34, width: 34 },
};

const useStyles = createStyles((theme) => ({
  container: {
    display: 'grid',
    transition: 'all 0.15s ease-in-out',
    gap: theme.spacing.sm,

    '&:not(:first-of-type)': {
      borderTop: `1px solid ${theme.colors.gray[2]}`,
    },

    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },

    '&.selected': {
      backgroundColor: theme.colors.blue_accent[0],
    },
  },
}));
